import type { UATGeofence } from '../../../services/fixedRouteUatClient';
import type { Environment } from './EnvironmentSelector';

interface CacheEntry {
  timestamp: number;
  data: UATGeofence[];
}

const GEOFENCES_CACHE_KEY = 'atlas_geofencesCache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

// In-memory cache for the current session
let sessionCache: Map<string, CacheEntry> = new Map();

// Initialize cache from sessionStorage on first load
if (typeof window !== 'undefined') {
  const savedCache = sessionStorage.getItem(GEOFENCES_CACHE_KEY);
  if (savedCache) {
    try {
      const parsedCache = JSON.parse(savedCache);
      // Reconstruct Map from plain object
      sessionCache = new Map(Object.entries(parsedCache).map(([key, value]) => [key, value as CacheEntry]));
      console.log('[geofencesCache] Loaded cache from sessionStorage:', sessionCache.size, 'entries');
    } catch (e) {
      console.error('[geofencesCache] Failed to parse cache from sessionStorage', e);
      sessionStorage.removeItem(GEOFENCES_CACHE_KEY);
    }
  }
}

function saveCacheToSessionStorage() {
  if (typeof window !== 'undefined') {
    // Convert Map to a plain object for sessionStorage
    const plainObjectCache = Object.fromEntries(sessionCache);
    sessionStorage.setItem(GEOFENCES_CACHE_KEY, JSON.stringify(plainObjectCache));
  }
}

/**
 * Generates a unique cache key for geofences list.
 */
function generateCacheKey(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): string {
  return `${environment}-${productId}-${startDate}-${endDate}-geofences`;
}

/**
 * Retrieves cached geofences list if available and not expired.
 */
export function getCachedGeofencesList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): UATGeofence[] | null {
  const key = generateCacheKey(environment, productId, startDate, endDate);
  const entry = sessionCache.get(key);

  if (entry && (Date.now() - entry.timestamp < CACHE_EXPIRATION_MS)) {
    console.log(`[geofencesCache] Cache hit for key: ${key}`);
    return entry.data;
  }

  if (entry) {
    console.log(`[geofencesCache] Cache expired for key: ${key}`);
    sessionCache.delete(key); // Remove expired entry
    saveCacheToSessionStorage();
  }

  return null;
}

/**
 * Stores geofences list in the cache.
 */
export function setCachedGeofencesList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string,
  data: UATGeofence[]
): void {
  const key = generateCacheKey(environment, productId, startDate, endDate);
  sessionCache.set(key, { timestamp: Date.now(), data });
  console.log(`[geofencesCache] Cache set for key: ${key}`);
  saveCacheToSessionStorage();
}

/**
 * Clears a specific entry from the cache.
 */
export function clearCachedGeofencesList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): void {
  const key = generateCacheKey(environment, productId, startDate, endDate);
  if (sessionCache.delete(key)) {
    console.log(`[geofencesCache] Cache cleared for key: ${key}`);
    saveCacheToSessionStorage();
  }
}

/**
 * Clears all entries from the cache.
 */
export function clearAllGeofencesCache(): void {
  sessionCache.clear();
  console.log('[geofencesCache] All cache cleared.');
  saveCacheToSessionStorage();
}

